import classNames from 'classnames';
import React, { CSSProperties, forwardRef, ReactNode } from 'react';

import styles from './Typography.module.css';

export type TypographyVariant =
  | 'header1'
  | 'header2'
  | 'header3'
  | 'header4'
  | 'subtitle'
  | 'lead'
  | 'body-semibold'
  | 'body-medium'
  | 'body-regular'
  | 'fineprint-regular'
  | 'fineprint-allcaps'
  | 'fineprint-semibold'
  | 'code';

export type TypographyElementType =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'div'
  | 'span'
  | 'label'
  | 'code'
  | 'pre'
  | 'li';

export interface TypographyProps {
  /**
   * The text content to show in the component.
   */
  children: ReactNode;
  /**
   * Optional additional class name to add to the element.
   */
  className?: string;
  /**
   * Unique ID for this element. Required when referring this element with aria HTML attributes.
   */
  id?: string;
  /**
   * Optional custom ARIA role for the helper text.
   */
  role?: string;
  /**
   * Optional additional inline CSS style to add to the element.
   */
  style?: CSSProperties;
  /**
   * The type of element tag to render.
   */
  tag: TypographyElementType;
  /**
   * The variant of the typograhy, defining the exact typography styling of the text.
   */
  variant: TypographyVariant;
}

const classNamesByVariant: Record<TypographyVariant, string> = {
  'body-medium': styles['body-medium'],
  'body-regular': styles['body-regular'],
  'body-semibold': styles['body-semibold'],
  code: styles.code,
  'fineprint-allcaps': styles['fineprint-allcaps'],
  'fineprint-regular': styles['fineprint-regular'],
  'fineprint-semibold': styles['fineprint-semibold'],
  header1: styles.header1,
  header2: styles.header2,
  header3: styles.header3,
  header4: styles.header4,
  lead: styles.lead,
  subtitle: styles.subtitle,
};

export type TypographyElementRefType = HTMLHeadingElement &
  HTMLParagraphElement &
  HTMLSpanElement &
  HTMLDivElement &
  HTMLLabelElement &
  HTMLPreElement &
  HTMLElement &
  HTMLLIElement;

/**
 * @deprecated This component will be removed in the next major release. Please use the UI5 Text, Title or Label component instead.
 *
 * Typography component renders a text element with one of the typography variants
 * from the Design System.
 *
 * It renders the defined HTML tag with font styling based on the defined variant.
 * Note that this component does _NOT_ reset any user agent specified styles for
 * that element. That should be done case by case by providing additional `className`
 * or with inline `style`.
 */
export const Typography = forwardRef<TypographyElementRefType, TypographyProps>(
  ({ children, variant, tag: Component, className, style, id, role }, ref) => {
    const variantClassName = classNamesByVariant[variant];
    if (!variantClassName) {
      throw new Error(`Invalid typography variant "${variant}"`);
    }

    return (
      <Component
        className={classNames(variantClassName, className)}
        id={id}
        ref={ref}
        role={role}
        style={style}
      >
        {children}
      </Component>
    );
  },
);

Typography.displayName = 'Typography';

export default Typography;
